import React from 'react';
import styled from 'styled-components';
import { FaFolder, FaImage, FaFileAlt, FaVideo, FaTrash } from 'react-icons/fa';
import { designTokens } from '../foundation/DesignTokens';
import { Drawer, DrawerList, DrawerItem } from './Drawer';

// Styled sidebar title
const SidebarTitle = styled.div`
  font-family: ${designTokens.fonts.heading};
  font-weight: 800;
  font-size: 1.25rem;
  color: ${designTokens.colors.gray[900]};
  margin-bottom: ${designTokens.spacing.xl};
  padding-left: 12px;
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.sm};

  @media (max-width: 900px) {
    font-size: 1.1rem;
    margin-bottom: ${designTokens.spacing.lg};
  }
`;

// Enhanced drawer item with better styling
const NavItem = styled(DrawerItem)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${designTokens.radius.md};
  color: ${designTokens.colors.gray[700]};
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  margin-bottom: 4px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  width: 100%;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: ${designTokens.colors.gray[50]};
    color: ${designTokens.colors.gray[900]};
  }

  ${(props) =>
    props.active &&
    `
    background-color: ${designTokens.colors.primary[50]};
    color: ${designTokens.colors.primary[600]};
    font-weight: 600;
    border-color: ${designTokens.colors.primary[100]};
  `}

  ${(props) =>
    props.disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
  `}

  @media (max-width: 900px) {
    font-size: 0.87rem;
    padding: 10px 14px;
    gap: 10px;
  }
`;

// Icon wrapper with colored background
const NavIconWrapper = styled.span`
  width: 28px;
  height: 28px;
  border-radius: ${designTokens.radius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
  background-color: ${props => props.bgColor || designTokens.colors.gray[100]};

  svg {
    font-size: 0.9rem;
    color: ${props => props.iconColor || designTokens.colors.gray[600]};
  }

  @media (max-width: 900px) {
    width: 26px;
    height: 26px;

    svg {
      font-size: 0.85rem;
    }
  }
`;

// Divider for separating trash section
const NavDivider = styled.div`
  height: 1px;
  background-color: ${designTokens.colors.gray[200]};
  margin: ${designTokens.spacing.md} 0;
`;

// Icon color configurations
const iconColors = {
  all: { bg: '#FEF3C7', icon: '#D97706' },      // Yellow/Amber
  images: { bg: '#FCE7F3', icon: '#DB2777' },    // Pink
  documents: { bg: '#DBEAFE', icon: '#2563EB' }, // Blue
  videos: { bg: '#D1FAE5', icon: '#059669' },    // Green
  trash: { bg: '#FEF3C7', icon: '#D97706' },     // Yellow/Amber
};

// Navigation items configuration for Multimedia
const navigationItems = [
  {
    id: 'all',
    label: 'Todos los archivos',
    Icon: FaFolder,
  },
  {
    id: 'images',
    label: 'Imágenes',
    Icon: FaImage,
  },
  {
    id: 'documents',
    label: 'Documentos',
    Icon: FaFileAlt,
  },
  {
    id: 'videos',
    label: 'Videos',
    Icon: FaVideo,
  },
];

const trashItem = {
  id: 'trash',
  label: 'Papelera',
  Icon: FaTrash,
};

const MultimediaNavigation = ({ activeView = 'all', onViewChange }) => {
  const handleNavClick = (item) => {
    if (item.disabled) return;

    if (onViewChange) {
      onViewChange(item.id);
    }
  };

  const renderNavItem = (item) => {
    const colors = iconColors[item.id] || iconColors.all;
    const IconComponent = item.Icon;

    return (
      <NavItem
        key={item.id}
        active={activeView === item.id}
        disabled={item.disabled}
        onClick={() => handleNavClick(item)}>
        <NavIconWrapper bgColor={colors.bg} iconColor={colors.icon}>
          <IconComponent />
        </NavIconWrapper>
        {item.label}
      </NavItem>
    );
  };

  return (
    <Drawer>
      <SidebarTitle>Galería</SidebarTitle>

      <DrawerList>
        {navigationItems.map(renderNavItem)}

        <NavDivider />

        {renderNavItem(trashItem)}
      </DrawerList>
    </Drawer>
  );
};

export default MultimediaNavigation;
